import { useMemo } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PERMISSIONS, type PermissionCode } from '@/lib/permissions'

/**
 * What this user may read in the reporting module.
 *
 * Every code below comes from `src/lib/permissions.ts`, which the server emits from its own
 * catalogue — see `ledger/permissions.ts` for why nothing here is a hand-typed string.
 *
 * Presentation only, as everywhere else: the server re-checks every request against the live
 * catalogue, so a hidden card gates nothing and a visible one grants nothing.
 *
 * The catalogue does not publish a code for every endpoint this module serves, and the mapping
 * below is this client's best reading of the six view codes against the nine reads:
 *
 * - `reports.par.view` gates both **portfolio at risk** and **arrears ageing** — the same finding,
 *   summarised and at facility level.
 * - `reports.collections.view` gates the **collections register**.
 * - `reports.officer_performance.view` gates **performance**, whichever dimension it is read at —
 *   the catalogue publishes one performance code rather than one per dimension, and this is the
 *   closest-named of the six.
 * - `reports.group_health.view` gates **group health**.
 * - `reports.portfolio.view` gates the **disbursements register** — there is no code named for
 *   disbursements specifically, and this is the remaining general "portfolio" read.
 * - `reports.trialbalance.view` gates the reporting module's own **trial balance**, the **balance
 *   sheet** and the **income statement** — all three are general-ledger financial statements under
 *   the catalogue's one view code for the ledger's own figures, the server's own reading of it too
 *   (see `ReportAccessGuard.PermissionFor` in the API). It also gates the **snapshot browser**,
 *   loosely: a snapshot can hold any report type and the server filters the list to what the caller
 *   may see, so this is the nearest single code for "has business freezing a report" rather than a
 *   precise one.
 *
 * `reports.export` and `reports.export.bulk` are read the same way everywhere: the export buttons
 * on a screen are gated on the screen's own view permission *and* `reports.export`, because
 * exporting a report is not a wider right than reading it — in every format, CSV, Excel or PDF alike.
 * `reports.bureau.extract`, `reports.regulatory.generate` and `reports.statement.generate` are
 * catalogued and held here for the capability-gap list, but no endpoint in the published contract
 * serves any of the three yet.
 */
export const REPORTS_PERMISSIONS = {
  bureauExtract: PERMISSIONS.reports.bureauExtract,
  collectionsView: PERMISSIONS.reports.collectionsView,
  exceptionView: PERMISSIONS.reports.exceptionView,
  export: PERMISSIONS.reports.export,
  exportBulk: PERMISSIONS.reports.exportBulk,
  groupHealthView: PERMISSIONS.reports.groupHealthView,
  officerPerformanceView: PERMISSIONS.reports.officerPerformanceView,
  parView: PERMISSIONS.reports.parView,
  portfolioView: PERMISSIONS.reports.portfolioView,
  regulatoryGenerate: PERMISSIONS.reports.regulatoryGenerate,
  statementGenerate: PERMISSIONS.reports.statementGenerate,
  trialBalanceView: PERMISSIONS.reports.trialBalanceView,
} as const satisfies Record<string, PermissionCode>

/** Any one of these is enough to have business in the reporting module at all. */
export const REPORTS_MODULE_PERMISSIONS: readonly PermissionCode[] = [
  REPORTS_PERMISSIONS.parView,
  REPORTS_PERMISSIONS.collectionsView,
  REPORTS_PERMISSIONS.officerPerformanceView,
  REPORTS_PERMISSIONS.groupHealthView,
  REPORTS_PERMISSIONS.portfolioView,
  REPORTS_PERMISSIONS.trialBalanceView,
]

export interface ReportsPermissions {
  canEnterModule: boolean
  canViewPar: boolean
  canViewCollections: boolean
  canViewDisbursements: boolean
  canViewPerformance: boolean
  canViewGroupHealth: boolean
  canViewTrialBalance: boolean
  /** Same permission as {@link canViewTrialBalance} — see this file's own note on why. */
  canViewBalanceSheet: boolean
  /** Same permission as {@link canViewTrialBalance}. */
  canViewIncomeStatement: boolean
  /** Same permission as {@link canViewTrialBalance} — the nearest single code for the snapshot browser. */
  canBrowseSnapshots: boolean
  canExport: boolean
  canExportBulk: boolean
  /** The SDI prudential return schedules, served by the Compliance module under a reports code. */
  canGenerateRegulatory: boolean
  /** The credit-bureau submission file, same arrangement. */
  canExtractBureau: boolean
}

export function useReportsPermissions(): ReportsPermissions {
  const { hasPermission } = useAuth()

  return useMemo(
    () => ({
      canEnterModule: hasPermission(REPORTS_PERMISSIONS.parView) ||
        hasPermission(REPORTS_PERMISSIONS.collectionsView) ||
        hasPermission(REPORTS_PERMISSIONS.officerPerformanceView) ||
        hasPermission(REPORTS_PERMISSIONS.groupHealthView) ||
        hasPermission(REPORTS_PERMISSIONS.portfolioView) ||
        hasPermission(REPORTS_PERMISSIONS.trialBalanceView),
      canViewPar: hasPermission(REPORTS_PERMISSIONS.parView),
      canViewCollections: hasPermission(REPORTS_PERMISSIONS.collectionsView),
      canViewDisbursements: hasPermission(REPORTS_PERMISSIONS.portfolioView),
      canViewPerformance: hasPermission(REPORTS_PERMISSIONS.officerPerformanceView),
      canViewGroupHealth: hasPermission(REPORTS_PERMISSIONS.groupHealthView),
      canViewTrialBalance: hasPermission(REPORTS_PERMISSIONS.trialBalanceView),
      canViewBalanceSheet: hasPermission(REPORTS_PERMISSIONS.trialBalanceView),
      canViewIncomeStatement: hasPermission(REPORTS_PERMISSIONS.trialBalanceView),
      canBrowseSnapshots: hasPermission(REPORTS_PERMISSIONS.trialBalanceView),
      canExport: hasPermission(REPORTS_PERMISSIONS.export),
      canExportBulk: hasPermission(REPORTS_PERMISSIONS.exportBulk),
      canGenerateRegulatory: hasPermission(REPORTS_PERMISSIONS.regulatoryGenerate),
      canExtractBureau: hasPermission(REPORTS_PERMISSIONS.bureauExtract),
    }),
    [hasPermission],
  )
}
